import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const RecentActivity = ({ activities }) => {
  const navigate = useNavigate();

  const getActivityIcon = (type) => {
    switch (type) {
      case 'incident_created':
        return { icon: 'Plus', color: 'text-primary' };
      case 'incident_updated':
        return { icon: 'Edit', color: 'text-accent' };
      case 'incident_resolved':
        return { icon: 'CheckCircle', color: 'text-success' };
      case 'incident_escalated':
        return { icon: 'TrendingUp', color: 'text-warning' };
      case 'comment_added':
        return { icon: 'MessageCircle', color: 'text-secondary' };
      case 'file_uploaded':
        return { icon: 'Upload', color: 'text-accent' };
      case 'assignment_changed':
        return { icon: 'UserCheck', color: 'text-primary' };
      case 'status_changed':
        return { icon: 'RefreshCw', color: 'text-warning' };
      default:
        return { icon: 'Activity', color: 'text-muted-foreground' };
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const handleActivityClick = (activity) => {
    if (activity?.incidentId) {
      navigate(`/incident-details?id=${activity?.incidentId}`);
    }
  };

  const handleViewAll = () => {
    navigate('/incidents-list?view=activity');
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-light">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Recent Activity</h3>
          <p className="text-sm text-muted-foreground">
            Latest updates across all incidents
          </p>
        </div>
        <button
          onClick={handleViewAll}
          className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-primary hover:bg-primary/10 rounded-md transition-colors duration-200"
        >
          <span>View All</span>
          <Icon name="ArrowRight" size={16} />
        </button>
      </div>
      {activities?.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Activity" size={24} className="text-muted-foreground" />
          </div>
          <h4 className="text-lg font-medium text-foreground mb-2">No recent activity</h4>
          <p className="text-sm text-muted-foreground">Activity will appear here as incidents are updated.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {activities?.slice(0, 8)?.map((activity) => {
            const activityStyle = getActivityIcon(activity?.type);
            
            return (
              <div
                key={activity?.id}
                onClick={() => handleActivityClick(activity)}
                className={`flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors duration-200 ${
                  activity?.incidentId ? 'cursor-pointer' : ''
                }`}
              >
                <div className={`p-2 rounded-full bg-card border border-border ${activityStyle?.color}`}>
                  <Icon name={activityStyle?.icon} size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-foreground truncate">
                      {activity?.title}
                    </p>
                    <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                      {formatTimeAgo(activity?.timestamp)}
                    </span>
                  </div>
                  
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                    {activity?.description}
                  </p>
                  
                  <div className="flex items-center space-x-3 text-xs text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Icon name="User" size={10} />
                      <span>{activity?.user}</span>
                    </div>
                    
                    {activity?.incidentId && (
                      <div className="flex items-center space-x-1">
                        <Icon name="Hash" size={10} />
                        <span>{activity?.incidentId}</span>
                      </div>
                    )}
                    
                    {activity?.severity && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        activity?.severity === 'critical' ? 'bg-error/10 text-error' :
                        activity?.severity === 'high' ? 'bg-warning/10 text-warning' :
                        activity?.severity === 'medium'? 'bg-accent/10 text-accent' : 'bg-success/10 text-success'
                      }`}>
                        {activity?.severity}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {activities?.length > 8 && (
            <div className="text-center pt-4 border-t border-border">
              <button
                onClick={handleViewAll}
                className="text-sm font-medium text-primary hover:text-primary/80 transition-colors duration-200"
              >
                View {activities?.length - 8} more activities
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RecentActivity;